import 'css/src/components/common/LogoImages';

import React, { useEffect, useRef } from 'react';

type LogoImagesRowProps = {
    logos: string[];
    direction: -1 | 1;
    lazyLoad: boolean;
};

type LogoImagesProps = {
    logos: string[];
    rows?: number;
    lazyLoad?: boolean;
};

const LogoImagesRow = React.memo(function LogoImages({
    logos,
    direction,
    lazyLoad,
}: LogoImagesRowProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<number>();
    const baseXRef = useRef(0);
    const lastScrollYRef = useRef(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let velocityFactor = 0;
        const baseVelocity = 0.5;
        const damping = 0.95;

        const animate = () => {
            const currentScrollY = window.scrollY;
            const scrollDelta = currentScrollY - lastScrollYRef.current;
            lastScrollYRef.current = currentScrollY;

            // Update velocity based on scroll
            velocityFactor = velocityFactor * damping + scrollDelta * 0.02;

            // Apply base velocity plus scroll velocity
            baseXRef.current += baseVelocity + velocityFactor;

            // Reset position when one set of logos has scrolled past
            const singleSetWidth = container.scrollWidth / 4; // Since we have 4 repetitions
            if (baseXRef.current >= singleSetWidth) {
                baseXRef.current = 0;
            }

            if (baseXRef.current < 0) {
                baseXRef.current = 0;
            }

            if (direction === -1) {
                container.style.transform = `translateX(${baseXRef.current - singleSetWidth}px)`;
            } else {
                container.style.transform = `translateX(${-baseXRef.current}px)`;
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [direction]);

    // Create multiple repetitions for seamless infinite loop
    const duplicatedLogos = [...logos, ...logos, ...logos, ...logos];

    return (
        <div className="logos-container-wrapper mb-l5-64">
            <div ref={containerRef} className="logos-container">
                {duplicatedLogos.map((logo, index) => {
                    return (
                        <img
                            className="business-logos mx-l5-32"
                            src={logo}
                            key={index}
                            loading={lazyLoad ? 'lazy' : 'eager'}
                            decoding={lazyLoad ? 'async' : 'auto'}
                            alt={`Company logo ${index}`}
                            // @ts-expect-error React 18 has a bug not recognizing fetchPriority: https://github.com/facebook/react/issues/28948
                            // eslint-disable-next-line react/no-unknown-property
                            fetchpriority={lazyLoad ? 'auto' : 'high'}
                        />
                    );
                })}
            </div>
        </div>
    );
});

const LogoImages = React.memo(function LogoImages({
    logos,
    rows,
    lazyLoad = true,
}: LogoImagesProps) {
    const logoRows = React.useMemo(() => {
        const groupSize = Math.ceil(logos.length / (rows || 1));
        const logoRows: string[][] = [];

        logos.forEach((logo, index) => {
            if (index % groupSize === 0) {
                logoRows.push([]);
            }
            logoRows[logoRows.length - 1].push(logo);
        });

        return logoRows;
    }, [logos, rows]);

    return (
        <div className="py-l5-48">
            {logoRows.map((logoRow, index) => {
                return (
                    <LogoImagesRow
                        key={index}
                        logos={logoRow}
                        direction={index % 2 === 0 ? 1 : -1}
                        lazyLoad={lazyLoad}
                    />
                );
            })}
        </div>
    );
});

export default LogoImages;
